import type { Notification } from '@scrambl/shared'
import { NotificationType } from '@scrambl/shared'

type NotificationData = Record<string, unknown>

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const buildQuery = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value)
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

function resolveTimesheetNotificationRoute(
  orgSlug: string,
  notification: Notification,
): string | undefined {
  const data = (notification.data || {}) as NotificationData
  const recipientRole = asString(data.recipientRole)
  const action = asString(data.action)
  const entryId = asString(data.entryId)
  const payPeriodId = asString(data.payPeriodId)
  const targetUserId =
    asString(data.targetUserId) || asString(data.userId) || notification.actorId

  if (recipientRole === 'APPROVER' && targetUserId) {
    return `/${orgSlug}/timesheets/approvals/${targetUserId}${buildQuery({
      periodId: payPeriodId,
      entryId,
    })}`
  }

  if (
    recipientRole === 'OWNER' &&
    (action === 'rejected' || action === 'changes_requested') &&
    entryId
  ) {
    if (payPeriodId) {
      return `/${orgSlug}/my-time/timesheets${buildQuery({
        payPeriodId,
        entryId,
      })}`
    }
    return `/${orgSlug}/my-time/timesheets${buildQuery({ entryId })}`
  }

  if (recipientRole === 'OWNER' && payPeriodId) {
    return `/${orgSlug}/my-time/timesheets${buildQuery({ payPeriodId })}`
  }

  if (entryId) {
    return `/${orgSlug}/my-time/timesheets${buildQuery({ entryId })}`
  }

  return undefined
}

export function getNotificationRoute(
  orgSlug: string | undefined,
  notification: Notification,
): string | undefined {
  if (!orgSlug) return undefined

  if (
    notification.type === NotificationType.TIMESHEET_ENTRY_SUBMITTED ||
    notification.type === NotificationType.TIMESHEET_ENTRY_APPROVED ||
    notification.type === NotificationType.TIMESHEET_ENTRY_REJECTED ||
    notification.type === NotificationType.TIMESHEET_ENTRY_CHANGES_REQUESTED ||
    notification.type === NotificationType.TIMESHEET_SUBMITTED ||
    notification.type === NotificationType.TIMESHEET_APPROVED ||
    notification.type === NotificationType.TIMESHEET_REJECTED ||
    notification.type === NotificationType.TIMESHEET_CHANGES_REQUESTED ||
    notification.type === NotificationType.TIMESHEET_CLOSE_REMINDER ||
    notification.type === NotificationType.TIMESHEET_MISSING_REMINDER ||
    notification.type === NotificationType.TIMESHEET_APPROVAL_DEADLINE
  ) {
    return resolveTimesheetNotificationRoute(orgSlug, notification)
  }

  if (
    notification.type === NotificationType.IMPORT_COMPLETED ||
    notification.type === NotificationType.IMPORT_FAILED
  ) {
    const moduleRoutes: Record<string, string> = {
      TASK: 'tasks',
      CONTACT: 'contacts',
      LEAD: 'leads',
      DEAL: 'deals',
      ENTITY: 'clients',
      PROJECT: 'projects',
    }
    const moduleType = asString(notification.data?.moduleType)
    return `/${orgSlug}/${moduleRoutes[moduleType || ''] || 'tasks'}`
  }

  // Agent review requests land in the review queue, not the task itself
  if (notification.type === 'AGENT_REVIEW_REQUESTED') {
    return `/${orgSlug}/tasks/reviews`
  }

  // Meeting recordings: ready notes open the generated note; failures (or
  // notes-less recordings) land on the recordings list.
  if (
    notification.type === NotificationType.MEETING_NOTES_READY ||
    notification.type === NotificationType.MEETING_NOTES_FAILED
  ) {
    const noteId = asString(notification.data?.noteId)
    if (notification.type === NotificationType.MEETING_NOTES_READY && noteId) {
      return `/${orgSlug}/notes/${noteId}`
    }
    return `/${orgSlug}/notes/recordings`
  }

  if (!notification.entityType) return undefined
  if (!notification.entityId && notification.entityType !== 'TICKET') {
    return undefined
  }

  switch (notification.entityType) {
    case 'TASK': {
      const taskIdentifier =
        asString(notification.data?.taskNumber) || notification.entityId
      return `/${orgSlug}/tasks/${taskIdentifier}`
    }
    case 'TICKET': {
      const ticketIdentifier =
        asString(notification.data?.ticketNumber) ||
        asString(notification.entityId) ||
        asString(notification.data?.ticketId)
      if (!ticketIdentifier) return undefined
      return `/${orgSlug}/tickets/${ticketIdentifier}`
    }
    case 'PROJECT':
      return `/${orgSlug}/projects/${notification.entityId}`
    case 'THREAD':
      return `/${orgSlug}/threads?tab=discussions&thread=${notification.entityId}`
    case 'NOTE': {
      const noteId =
        asString(notification.data?.noteId) || notification.entityId
      return `/${orgSlug}/notes/${noteId}`
    }
    case 'ENTITY':
      return `/${orgSlug}/entities/${notification.entityId}`
    case 'DEAL':
      return `/${orgSlug}/deals/${notification.entityId}`
    case 'DEMAND':
      return `/${orgSlug}/team/demand/${notification.entityId}`
    default:
      return undefined
  }
}
